<template>
  <div class="privacy">
    <header class="header">
      <div class="container">
        <div class="header-content">
          <div class="logo-container">
            <img src="@/assets/logo.png" alt="Forms Pro Logo" class="logo-img" />
            <h1 class="logo">Forms Pro for Google Forms</h1>
          </div>
          <nav class="nav">
            <router-link to="/" class="nav-link">Home</router-link>
            <a href="/privacy" class="nav-link">Privacy</a>
            <a href="/terms" class="nav-link">Terms</a>
            <router-link to="/subscription" class="nav-link">Subscription</router-link>
          </nav>
        </div>
      </div>
    </header>

    <main class="main">
      <div class="container">
        <div class="content-wrapper">
          <h1 class="page-title">Privacy Policy</h1>
          <p class="update-date"><strong>Last modified:</strong> August 17, 2026</p>

          <div class="quick-links">
            <p>
              <strong>Interest based advertising:</strong> <a href="#advertising" class="inline-link">to learn more about data disclosure click here</a>
            </p>
            <p>
              <strong>Opt-out options:</strong> <a href="#opt-out" class="inline-link">to learn more about your choices click here</a>
            </p>
          </div>

          <section class="section intro">
            <h2>I. INTRODUCTION</h2>
            <p>
              Yikens Technology HK Limited ("we," "us" or "our") takes your privacy seriously. This Privacy policy ("Privacy policy") explains our data protection policy and describes the types of information we may process when you install and/or use "Forms Pro for Google Forms" software application for mobile devices (the "App", "our App") and explains how we and some of the companies we cooperate while processing that information.
            </p>
            <p>
              When we refer to personal data (or personal information) we mean any information of any kind relating to a natural person who can be identified, directly or indirectly, in particular by reference to such data.
            </p>
            <p>
              It is a natural person who can be identified directly or indirectly, in particular by reference to an identification number or to one or more factors specific to his or her physical, physiological, mental, economic, cultural or social status.
            </p>
            <p>
              Our Privacy policy applies to all users, and others who access the App ("Users").
            </p>
            <p>
              For the purposes of the GDPR, we are the data controller, unless otherwise stated.
            </p>
            <div class="important-note">
              <p>
                <strong>PLEASE READ THE FOLLOWING PRIVACY POLICY, FOR INFORMATION REGARDING THE WAYS YOUR PERSONAL INFORMATION MAY BE PROCESSED, CAREFULLY. WHEN YOU USE THE APP YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS.</strong>
              </p>
            </div>
          </section>

          <section class="section">
            <h2>II. INFORMATION WE PROCESS</h2>
            <p>
              There are two general categories of information we can process.
            </p>

            <h3>II.1 Information that you submit</h3>
            <p>
              We process the following personal information about you when you use the App. This information is necessary for the adequate performance of the contract between you and us. Without such information it is impossible to provide complete functionality of the App and perform the requested services.
            </p>

            <h4>Usage of Google Forms data on your Google Drive</h4>
            <p>
              This application only uses data related to user's Google Forms on their Google Drive. No Google Forms data is collected and/or shared with any third-party application. For detailed information about how we handle Google user data, please see the Google API Services User Data Policy section below, including the Additional Requirements for Specific API Scopes.
            </p>

            <h4>Log Data</h4>
            <p>
              In case of an error while using our app, we collect data and information (through third party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol ("IP") address, device name, operating system version, the configuration of the app when utilizing my Service, the time and date of your use of the Service, and other statistics.
            </p>

            <h3>II.2 Information That Is Processed Automatically</h3>
            <p>
              When you use the App, some information about your device and your user behaviour may be processed automatically. This information is generally non-personal, i.e. it does not, on its own, permit direct association with any specific individual, and we may access it only in aggregated form. We process this information on the ground of our legitimate interest in improving our App and giving our users the best experience. If we do not access such data we may not be able to provide you with all the requested services and features of the App.
            </p>
            <p>
              We use third-party automatic data processing technologies to analyze certain information sent by your device or our App. Automatic data processing technologies (advertising or analytics tools) provide us with the data described below. We do not control, supervise or respond for how the third parties process your personal data, that might be collected by their own means (not through our App). Due to the fact that some of our third parties (such as MoPub) declare themselves controllers for the purposes of the GDPR, any information request regarding your personal data should be directed to such third parties.
            </p>

            <h4>Information related to your device.</h4>
            <p>
              When you use a mobile device (tablet / phone / smartwatch) to access our App, we may monitor one or more "device identifiers". Device identifiers are small data files or similar data structures stored on or associated with your mobile device, which uniquely identify your mobile device. Device identifier enables generalized reporting or personalized content and ads by the third parties.
            </p>
            <p><strong>What data can be processed:</strong></p>
            <ul>
              <li>type of your mobile device and operational system version which is installed on your mobile device;</li>
              <li>region information of your mobile device;</li>
              <li>region associated with your SIM card;</li>
              <li>Internet Protocol ("IP") address;</li>
              <li>Identity For Advertisers / Advertising ID which is a string of numbers and letters that identifies your individual smartphone or tablet.</li>
            </ul>

            <h4>Log file information.</h4>
            <p>
              Log file information is automatically reported each time you make a request to access the App. It can also be provided when the App is installed on your device. When you use our App, analytics tools automatically record certain log file information, including time and date when you start and stop using the App, and how you interact with the App.
            </p>
            <p>
              The App may use automated processing of your personal data, including profiling, which means any form of automated processing of personal data used to evaluate certain personal aspects relating to you, in particular to analyze or predict aspects concerning your personal preferences, interests, behavior, location or movements. Processing information through automatic data processing technologies starts automatically when you start using the App.
            </p>

            <h4>Cookies and similar technologies.</h4>
            <p>
              When you use the App, we may use cookies and similar technologies like pixels, web beacons, scripts to process information about how you use our App and provide features to you. A cookie is a text file containing small amounts of information which is downloaded to your device when you access the App. The text file is then sent back to our server each time you use the App. This enables us to operate the App more effectively. For example, we will know how many users access a specific areas or features within our App and which links or ads they clicked on. We use this aggregated information to understand and optimize how our App is used, improve our marketing efforts, and provide content and features that are of interest to you. We may ask advertisers or other partners to serve ads or services to the App, which may use cookies or similar technologies.
            </p>
          </section>

          <section class="section">
            <h2>III. THE PURPOSES OF PROCESSING YOUR PERSONAL DATA</h2>
            <p>
              Our mission is to constantly improve our App and provide you with new experiences. As part of this mission, we use your information for the following purposes:
            </p>
            <ol>
              <li><strong>To make our service available.</strong> We use information that you submit and information that is processed automatically to provide you with all requested services.</li>
              <li><strong>To improve, test and monitor the effectiveness of our App.</strong> We use information that is processed automatically to better understand user behavior and trends, detect potential outages and technical issues, to operate, protect, improve, and optimize our App.</li>
              <li><strong>To provide you with interest-based (behavioral) advertising or other targeted content.</strong> We may use non-Google data that is processed automatically, such as device and advertising identifiers, for marketing purposes. Google API user data is never used for advertising, ad personalization, or marketing.</li>
              <li><strong>To communicate with you.</strong> We use the information we have to communicate with you through newsletters, i.e. to send you marketing notifications, receive your feedback about our App experience, and let you know about our policies and terms. We also use your information to respond to you when you contact us.</li>
              <li><strong>To prevent fraud and spam, to enforce the law.</strong> We really want our App to be free of spam and fraudulent content so that you feel safe and free. We may use your information to prevent, detect, and investigate fraud, security breaches, potentially prohibited or illegal activities, protect our trademarks and enforce our Terms of Use.</li>
            </ol>
            <p>
              If any new purposes for processing your personal data arise we will let you know we start to process information on that other purpose by introducing the corresponding changes to this Privacy policy.
            </p>

            <h3 id="google-data">Google User Data Access, Use, and Protection</h3>
            <p>
              Forms Pro for Google Forms accesses Google user data only after the user gives explicit permission through Google OAuth. Access is limited to the data required to provide a feature that the user chooses to use. The permissions shown on the Google OAuth consent screen are the authoritative list of permissions requested by the current version of the App.
            </p>

            <h4>Google Data and Permissions We Use</h4>
            <p>
              Depending on the features the user selects and the permissions the user grants, the App may access the following categories of Google user data:
            </p>
            <ul>
              <li><strong>Google Forms permission:</strong> used to create forms, read and edit form structure, and read responses for forms that the user selects or manages through the App.</li>
              <li><strong>Google Drive permission:</strong> used to find, open, and manage Google Forms and related files that the user explicitly selects, owns, or creates through the App.</li>
              <li><strong>Google Sheets permission:</strong> used to create, read, and update spreadsheets associated with form responses when the user enables a spreadsheet-related workflow.</li>
            </ul>

            <h4>How We Use the Data</h4>
            <p>
              The accessed Google user data is used solely to:
            </p>
            <ul>
              <li>Create or update a form at the user's direction;</li>
              <li>Display, process, and synchronize form responses requested by the user;</li>
              <li>Write and update data in user-owned spreadsheets when the user enables that feature;</li>
              <li>Open and manage related files selected by the user; and</li>
              <li>Provide form automation and reporting workflows initiated by the user.</li>
            </ul>

            <h4>Data Storage, Retention, and Deletion</h4>
            <ul>
              <li>The App does not permanently retain Google Forms content, Google Drive file content, or Google Sheets content on our servers.</li>
              <li>Google user data may be processed temporarily only when necessary to complete a user-requested operation and is deleted when that processing is complete.</li>
              <li>Users can revoke the App's access at any time in their Google Account permissions. After access is revoked, the App can no longer retrieve Google user data.</li>
            </ul>

            <h4>Data Sharing, Advertising, and AI/ML Usage</h4>
            <ul>
              <li>We do not sell, rent, or transfer Google user data to advertising platforms, data brokers, or other third parties.</li>
              <li>Google user data is not used for advertising, marketing, user profiling, or training generalized AI or machine-learning models.</li>
              <li>AI-assisted form drafting is based on instructions that the user directly chooses to provide. Google user data is not used to train AI models.</li>
              <li>Our use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" class="inline-link">Google API Services User Data Policy</a>, including the Limited Use requirements.</li>
            </ul>

            <h4>User Consent for Data Processing Related to Refund Requests</h4>
            <p>
              In accordance with the App Store's refund policy, when a user initiates a refund request, our server will receive a notification (CONSUMPTION_REQUEST) from Apple. In such cases, we will provide Apple with data related to the user's subscription to assist Apple in making a decision regarding the refund request.
            </p>
            <p>
              Please note that the data provided is strictly limited to subscription-related consumption information and does not include any personal or sensitive data. We are committed to complying with Apple's requirements and ensuring that all data processing activities fully adhere to privacy protection laws and regulations.
            </p>
            <p>
              By using this app, you consent to the transmission of your subscription consumption data to Apple, as required for processing refund requests, in compliance with the App Store's refund policies.
            </p>
          </section>

          <section class="section" id="advertising">
            <h2>IV. SHARING OF YOUR INFORMATION</h2>
            <p>
              We will share your information with third parties only in the ways that are described in this Privacy policy. We adhere to the U.S. Digital Advertising Alliance's Self-Regulatory Principles for the Mobile Environment.
            </p>
            <p>
              We will not rent or sell your personal data to any third parties, but we may share non-Google information from tools such as cookies, log files, device identifiers, and location data with the service providers described below. This sharing never includes Google Forms content, Google Drive file content, Google Sheets content, or other data received from Google APIs.
            </p>
            <p>
              We may also share non-Google information such as cookie or advertising identifier data with advertising partners for ad delivery. Google API user data is never shared with advertising partners and is never used to personalize advertisements.
            </p>
            <p>
              We may engage the following third parties in order to provide us with necessary infrastructure for delivery and improvement of our services:
            </p>

            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Entity name</th>
                    <th>Services performed</th>
                    <th>Entity location</th>
                    <th>Link to Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Apple Inc.</td>
                    <td>Cloud storage provider (for iOS devices)</td>
                    <td>U.S.A.</td>
                    <td><a href="https://www.apple.com/lae/privacy/" target="_blank" rel="noopener noreferrer" class="table-link">Privacy Policy</a></td>
                  </tr>
                  <tr>
                    <td>AdMob (Google LLC)</td>
                    <td>Ad mediator</td>
                    <td>U.S.A.</td>
                    <td><a href="https://support.google.com/admob/answer/48182" target="_blank" rel="noopener noreferrer" class="table-link">Privacy Policy</a></td>
                  </tr>
                  <tr>
                    <td>Firebase (Google LLC)</td>
                    <td>Analytics service provider</td>
                    <td>U.S.A.</td>
                    <td><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" class="table-link">Privacy Policy</a></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              In case you want to learn more about these services and their privacy options (including opt-out) please consult the correspondent websites and privacy policies. We are not responsible for any usage of your personal data by the abovementioned third parties in violation of our instructions.
            </p>
            <p>
              Our App may contain links to third-party websites / services or you may access the App from a third-party site. We are not responsible for the privacy practices of these third-party sites or services linked to or from our App, including the information or content contained within them.
            </p>
            <p>
              We may disclose your personal information if it is needed for objective reasons, due to the public interest or in other unforeseen circumstances:
            </p>
            <ul>
              <li>as required by law;</li>
              <li>when we believe, in good faith, that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;</li>
              <li>if we are involved in a merger, acquisition, or sale of all or a portion of our assets, you will be notified via email and/or a prominent notice in our App of any change in ownership or your personal information usage, as well as any choices you may have regarding your personal information.</li>
            </ul>
          </section>

          <section class="section" id="opt-out">
            <h2>V. EXERCISING YOUR RIGHTS</h2>
            <p>
              When your personal information that is processed automatically you may object to such processing in some circumstances. Where your personal information is processed for direct marketing purposes, you may ask to cease processing your data for these direct marketing purposes. In order to exercise your right please contact the third party services listed in Section IV of this Privacy Policy to learn how you can object to processing your data. Most of them have clear instructions on their privacy pages, functional API or other options.
            </p>
            <p>
              Please note that you can opt-out of marketing tracking by choosing:
            </p>
            <ul>
              <li>option "Limit Ad Tracking" on your iOs device in Settings/ Privacy/ Advertising, please find additional information here: <a href="https://support.apple.com/en-us/HT202074" target="_blank" rel="noopener noreferrer" class="inline-link">https://support.apple.com/en-us/HT202074</a>;</li>
              <li>option "Opt out of Ad Personalization" on your Android device in Settings/ Google/ Ads.</li>
            </ul>
            <p>
              To opt-out of interest based advertising you can also visit <a href="https://youradchoices.com" target="_blank" rel="noopener noreferrer" class="inline-link">https://youradchoices.com</a>.
            </p>
            <p>
              Please mind when you opt out of certain interest based advertising, you may still continue to receive contextual ads based on other non-personal information, such as ads related to the content of other digital products you are using.
            </p>

            <h3>Opt-Out of the Location Data Processing</h3>
            <p>
              Certain third-party advertising partners may collect precise location data, or street-level location information about you ("Precise Location Data"). Your mobile device may allow you to opt out of the collection of Precise Location Data as further described below.
            </p>
            <p>
              <strong>iOS 11 or later:</strong><br>
              In order to disable the collection of Precise Location Data on iOS, you may turn Location Services off for the applicable Product via the menu "Settings > Privacy > Location Services". Then select the applicable App and set the "Share My Location" status to "Never". Please see additional information here: <a href="https://support.apple.com/en-us/HT203033" target="_blank" rel="noopener noreferrer" class="inline-link">https://support.apple.com/en-us/HT203033</a>.
            </p>
            <p>
              If you are located in the European Union you may address our representative when you have questions on privacy issues:
            </p>
            <p class="contact-info">
              <strong>E-mail:</strong> <a href="mailto:sl9283191@gmail.com" class="email-link">sl9283191@gmail.com</a>
            </p>
          </section>

          <section class="section">
            <h2>VI. SECURITY</h2>
            <p>
              The security of your personal information is highly important to us. We follow generally accepted industry standards to protect the personal information submitted to us, both during transmission and once we receive it.
            </p>
            <p>
              We take reasonable and appropriate measures to protect personal information from loss, misuse and unauthorized access, disclosure, alteration and destruction, taking into account the risks involved in the processing and the nature of the personal information.
            </p>
            <p>
              We implement appropriate technical and organizational measures, which are designed to implement data-protection principles, such as data minimization, in an effective manner and to integrate the necessary safeguards into the processing. We seek your personal data to be encrypted with proper and strong encryption algorithms, including hashing where possible.
            </p>
            <p>
              Unfortunately, no method of transmission over the Internet, or method of electronic storage, is 100% secure. We do our best to protect your personal data, nevertheless, we cannot guarantee its absolute security. In the event that your personal information is compromised as a breach of security, we will promptly notify you in compliance with applicable law.
            </p>
            <p>
              If you have any questions about the security of our App, you can contact us at through email or contact form displayed below.
            </p>
          </section>

          <section class="section">
            <h2>VII. CHILDREN'S PRIVACY</h2>
            <p>
              Our App is not intended for children under the age of 18. Therefore, we do not knowingly collect or solicit any personal information from children under 18. No one under age 18 may provide any personal information to the App. If you are under 18, do not use or provide any information on this App or through any of its features. Do not provide any information about yourself, including your email address. If we learn that we have collected personal information from a child under age 18 without verification of parental consent, we will erase that information as quickly as possible. If you believe that we might have any information from or about a child under 18, please contact us.
            </p>
          </section>

          <section class="section">
            <h2>VIII. CHANGES TO THE PRIVACY POLICY</h2>
            <p>
              This Privacy policy will be regularly updated.
            </p>
            <p>
              Whenever we change this Privacy policy, we will post those changes to this Privacy policy and other places that we consider appropriate. Additional forms of notice of modifications or updates as appropriate under the circumstances may be provided to you.
            </p>
          </section>

          <section class="section contact">
            <h2>IX. HOW TO CONTACT US</h2>
            <p>
              If you have any questions about this Privacy Policy, please feel free to contact us via support form.
            </p>
            <p class="contact-info">
              <strong>Email:</strong> <a href="mailto:sl9283191@gmail.com" class="email-link">sl9283191@gmail.com</a>
            </p>
          </section>

          <section class="section copyright">
            <p>
              Copyright © 2026 Yikens Technology HK Limited. All rights reserved.
            </p>
          </section>
        </div>
      </div>
    </main>

    <footer class="footer">
      <div class="container">
        <div class="footer-bottom">
          <p>&copy; 2026 Forms Pro for Google Forms. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Privacy'
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.privacy {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fc;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0;
  margin: 0;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: #666;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: color 0.3s;
  position: relative;
  padding-bottom: 2px;
  border-bottom: 2px solid transparent;
}

.nav-link:hover {
  color: #1a1a1a;
}

.nav-link.router-link-active {
  color: #4c51bf;
  border-bottom-color: #4c51bf;
}

/* Main Content */
.main {
  flex: 1;
  padding: 60px 0;
}

.content-wrapper {
  background: white;
  padding: 48px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  max-width: 900px;
  margin: 0 auto;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  text-align: center;
  letter-spacing: 0;
}

.update-date {
  text-align: center;
  color: #666;
  margin-bottom: 24px;
  font-weight: 600;
}

.quick-links {
  background: #f0f4ff;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 40px;
  border-left: 4px solid #4c51bf;
}

.quick-links p {
  margin-bottom: 8px;
}

.quick-links p:last-child {
  margin-bottom: 0;
}

.section {
  margin-bottom: 40px;
}

.section.intro {
  background: linear-gradient(135deg, #f0f4ff 0%, #e9ecff 100%);
  padding: 24px;
  border-radius: 12px;
  border-left: 4px solid #4c51bf;
  margin-bottom: 48px;
}

.section.intro p {
  margin-bottom: 12px;
}

.section.intro p:last-child {
  margin-bottom: 0;
}

.section h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.section h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 24px 0 16px;
}

.section h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin: 20px 0 12px;
}

.section p {
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 16px;
  font-size: 1rem;
}

.section p:last-child {
  margin-bottom: 0;
}

.section ul,
.section ol {
  margin: 16px 0 16px 24px;
}

.section ul {
  list-style-type: disc;
}

.section ol {
  list-style-type: decimal;
}

.section li {
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 8px;
  padding-left: 8px;
}

.section li strong {
  color: #1a1a1a;
  font-weight: 600;
}

.section strong {
  color: #1a1a1a;
  font-weight: 600;
}

.important-note {
  background: linear-gradient(135deg, #fff9e6 0%, #ffe9cc 100%);
  padding: 20px;
  border-radius: 12px;
  border-left: 4px solid #f59e0b;
  margin: 20px 0;
}

.important-note p {
  margin-bottom: 0;
}

.table-wrapper {
  overflow-x: auto;
  margin: 20px 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin: 20px 0;
}

.data-table thead {
  background: linear-gradient(135deg, #4c51bf 0%, #6366f1 100%);
  color: white;
}

.data-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 0.95rem;
}

.data-table td {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  font-size: 0.95rem;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover {
  background: #f8f9fc;
}

.table-link {
  color: #4c51bf;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s;
}

.table-link:hover {
  color: #5b21b6;
  text-decoration: underline;
}

.inline-link {
  color: #4c51bf;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s;
  border-bottom: 1px solid transparent;
}

.inline-link:hover {
  color: #5b21b6;
  border-bottom-color: #5b21b6;
}

.section.contact {
  background: #f8f9fc;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
}

.contact-info {
  font-size: 1.1rem;
  padding: 16px;
  background: white;
  border-radius: 8px;
  margin-top: 16px;
}

.email-link {
  color: #4c51bf;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border-bottom: 2px solid transparent;
}

.email-link:hover {
  color: #5b21b6;
  border-bottom-color: #5b21b6;
}

.section.copyright {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  margin-top: 40px;
  color: #666;
  font-size: 0.95rem;
}

/* Footer */
.footer {
  background: #1a1a1a;
  color: #999;
  padding: 32px 0;
}

.footer-bottom {
  text-align: center;
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }

  .nav {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .content-wrapper {
    padding: 32px 24px;
  }

  .page-title {
    font-size: 2rem;
  }

  .section h2 {
    font-size: 1.5rem;
  }

  .section h3 {
    font-size: 1.2rem;
  }

  .section h4 {
    font-size: 1rem;
  }

  .logo {
    font-size: 1.25rem;
  }

  .logo-img {
    height: 28px;
  }

  .logo-container {
    gap: 8px;
  }

  .data-table {
    font-size: 0.85rem;
  }

  .data-table th,
  .data-table td {
    padding: 12px 8px;
  }
}
</style>
